// src/hooks/useBlockedDomains.js
import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

// Helper: Domain normalisieren
function normalizeDomain(s) {
    if (!s) return "";
    let d = s.trim();
    d = d.replace(/^https?:\/\//i, ""); // remove protocol
    d = d.replace(/\/.*$/, "");         // remove path
    d = d.replace(/:+\d+$/, "");        // remove port
    return d.toLowerCase();
}

// Helper: einfache Domain-Validation
function isLikelyDomain(s) {
    return /^(?!-)(?:[a-z0-9-]{1,63}\.)+[a-z]{2,}$/i.test(s);
}

export default function useBlockedDomains() {
    const [domains, setDomains] = useState([]);   // [{id, domain, is_active, created_at, ai_rating, ai_reason, ai_probability, tags: []}, ...]
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const fetchBlocked = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await axios.get(`${API_BASE}/api/blocked-domains`, { timeout: 15000 });
            let data = Array.isArray(res.data) ? res.data.filter(Boolean) : [];

            // Tags für alle Domains holen und anhängen
            const withTags = await Promise.all(
                data.map(async (d) => {
                    try {
                        const tRes = await axios.get(
                            `${API_BASE}/api/blocked-domains/${d.id}/tags`,
                            { timeout: 15000 }
                        );
                        const tags = Array.isArray(tRes.data)
                            ? tRes.data.filter(Boolean)
                            : [];
                        return { ...d, tags };
                    } catch (e) {
                        console.error("Konnte Tags für Domain nicht laden:", d.id, e);
                        return { ...d, tags: [] };
                    }
                })
            );

            setDomains(withTags);
        } catch (e) {
            console.error(e);
            setError(e?.response?.data?.error || e?.message || "Konnte Blockliste nicht laden.");
            setDomains([]);
        } finally {
            setLoading(false);
        }
    }, []);

    // Domains hinzufügen (bulk, mit aktiv-Flag)
    const addDomains = useCallback(
        async (input, isActive = true, { ai_rating = false, ai_reason = null, ai_probability = null } = {}) => {
            let list = Array.isArray(input) ? input : String(input).split(/[\s,;\n]+/);
            list = list
                .map(normalizeDomain)
                .filter(Boolean)
                .filter(isLikelyDomain);

            list = Array.from(new Set(list));

            if (!list.length) {
                throw new Error("Keine gültigen Domains gefunden.");
            }

            await axios.post(
                `${API_BASE}/api/blocked-domains`,
                {
                    domains: list,
                    is_active: !!isActive,
                    ai_rating: !!ai_rating,
                    ai_reason,
                    ai_probability,
                },
                { timeout: 15000 }
            );
            await fetchBlocked();
            return list;
        },
        [fetchBlocked]
    );

    // Domain aktualisieren (z. B. aktiv/inaktiv)
    const updateDomain = useCallback(
        async (id, { domain, is_active, ai_rating, ai_reason, ai_probability } = {}) => {
            if (
                domain == null &&
                is_active == null &&
                ai_rating == null &&
                ai_reason == null &&
                ai_probability == null
            ) {
                return;
            }
            await axios.put(
                `${API_BASE}/api/blocked-domains/${id}`,
                { domain, is_active, ai_rating, ai_reason, ai_probability },
                { timeout: 15000 }
            );
            await fetchBlocked();
        },
        [fetchBlocked]
    );

    // Domain löschen
    const deleteDomain = useCallback(
        async (id) => {
            await axios.delete(`${API_BASE}/api/blocked-domains/${id}`, { timeout: 15000 });
            await fetchBlocked();
        },
        [fetchBlocked]
    );

    // Tag hinzufügen
    const addTag = useCallback(
        async (domainId, tag) => {
            await axios.post(
                `${API_BASE}/api/blocked-domains/${domainId}/tags`,
                { tag },
                { timeout: 15000 }
            );
            await fetchBlocked();
        },
        [fetchBlocked]
    );

    // Tag löschen
    const deleteTag = useCallback(
        async (domainId, tag) => {
            await axios.delete(
                `${API_BASE}/api/blocked-domains/${domainId}/tags`,
                {
                    data: { tag },
                    timeout: 15000,
                }
            );
            await fetchBlocked();
        },
        [fetchBlocked]
    );

    useEffect(() => {
        fetchBlocked();
    }, [fetchBlocked]);

    return {
        domains,
        loading,
        error,
        fetchBlocked,
        addDomains,
        updateDomain,
        deleteDomain,
        addTag,
        deleteTag,
        isLikelyDomain,
        normalizeDomain,
    };
}
